import type { Dispatch, SetStateAction } from 'react'
import { useRef } from 'react'
import React, { useContext as useContextCore } from 'react'
import type { Components, MentionInputType } from './constants'
import type { Activity } from '@ztechapps/common.contract'
import { ActivityList } from './ActivityList'
import { DeleteActivity } from './DeleteActivity'
import { useActivityRemarksAndHistory } from '@ztechapps/common.hook'
import orderBy from 'lodash/orderBy'
import merge from 'lodash/merge'
import { JsonViewer, Scrollable, TooltipPopOver } from '@ztechapps/webui.util'
import { truncate } from '@ztechapps/common.utils'
import { useToggle } from 'react-use'
import { AddActivityInput } from './DefaultInput/AddActivityInput'
import { UpdateActivityInput } from './DefaultInput/UpdateActivityInput'
import isEmpty from 'lodash/isEmpty'
import { AddMentionInput } from './MentionInput/AddMentionInput'
import { UpdateMentionInput } from './MentionInput/UpdateMentionInput'

export const defaultComponents = (mention: MentionInputType): Partial<Components> => {
  return {
    activityList: ActivityList,
    deleteActivityButton: DeleteActivity,
    addRemarksInput: !isEmpty(mention) ? AddMentionInput : AddActivityInput,
    updateRemarksInput: !isEmpty(mention) ? UpdateMentionInput : UpdateActivityInput,
  }
}

export type ActivityProviderProps = {
  components?: Components
  activities: Activity[]
  children: React.ReactNode
  disabled?: boolean
  height?: number | string
  activityFieldName?: string
  documentId?: string
  historyFilter?: Record<string, any>
  entityName: string
  mention?: MentionInputType
}

export type ActivityContextType = {
  showInput: boolean
  setShowInput: Dispatch<SetStateAction<boolean>>
  listContainerRef: React.MutableRefObject<HTMLDivElement>
  onKeyDown: (e: React.KeyboardEvent<any>, onSubmit: (e: React.KeyboardEvent<any>) => void) => void
  reset: () => void
  setShowHistory?: (nextValue?: any) => void
  showHistory?: boolean
} & Omit<ActivityProviderProps, 'children'>

const context = React.createContext<ActivityContextType>({} as ActivityContextType)
const CoreProvider = context.Provider

export const ActivityProvider = ({
  children,
  activities,
  components,
  height = '100%',
  documentId,
  historyFilter,
  mention,
  ...props
}: ActivityProviderProps) => {
  const [showHistory, setShowHistory] = useToggle(false)

  const { activities: activityAndHistory } = useActivityRemarksAndHistory({
    remarksActivity: activities,
    historyFilter,
    component: ({ item }) => {
      return (
        <TooltipPopOver
          showHeader={true}
          tooltipBody={
            <Scrollable height="300px">
              <div style={{ width: 400 }}>
                <JsonViewer data={item.diff} collapsed={false} />
              </div>
            </Scrollable>
          }
          transition={false}
          tooltipHeader={item.author}
          placement={'bottom-start'}
          flip
        >
          <small>
            <b>{item.author}</b>
            <span> {truncate(item.description, 200)}</span>
          </small>
        </TooltipPopOver>
      )
    },
    showHistory,
  })

  const sortedActivities = React.useMemo(
    () => orderBy(activityAndHistory, ['updatedAt'], ['desc']),
    [activityAndHistory]
  )

  const mergedComponents = React.useMemo(() => merge({}, defaultComponents(mention), components), [components, mention])
  const [showInput, setShowInput] = React.useState(false)
  const listContainerRef = useRef<HTMLDivElement>(null)

  const reset = () => {
    //scrollback to the top
    if (listContainerRef?.current) {
      listContainerRef.current.scrollTop = 0
    }
  }

  const onKeyDown = async (e: React.KeyboardEvent<any>, onSubmit: (e: React.KeyboardEvent<any>) => void) => {
    if (e.key === 'Escape') {
      reset()
    }
    //else if key was Shift + Enter, then we need to submit
    else if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault()
      onSubmit(e)
    }
  }

  return (
    <CoreProvider
      value={{
        components: mergedComponents,
        activities: sortedActivities,
        showInput,
        setShowInput,
        listContainerRef,
        height,
        onKeyDown,
        reset,
        documentId,
        setShowHistory,
        showHistory,
        historyFilter,
        mention,
        ...props,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useActivityContext = () => useContextCore(context)
